/**
 * Survives a page reload in the middle of a live appointment.
 *
 * Reloading (or a tab crash, or a stray navigation) used to drop the customer at
 * the top of the funnel — Preferences, then the permission gate — while the
 * meeting was still running and the Portal call still open. On the 2026-07-29
 * demo that was the customer's only escape from a stuck video switch, and it cost
 * them the whole entry sequence.
 *
 * Scope is deliberately `sessionStorage`, keyed by room: it survives a reload of
 * the same tab and nothing else. A brand-new tab, a second device or a link
 * opened tomorrow still gets the full funnel — including the permission gate,
 * which is the part that would be dangerous to skip on an unknown browser.
 */

import type { CommunicationMode } from '../types/app';

const KEY_PREFIX = 'immerss.appointment.';

export interface ResumeState {
  /** Mode the customer was last in — restored so the reload is invisible. */
  mode: CommunicationMode;
  /** Recording-consent decision already given; null when never asked. */
  consented: boolean | null;
}

const MODES: readonly CommunicationMode[] = ['video', 'audio', 'chat'];

function storageKey(roomKey: string): string {
  return `${KEY_PREFIX}${roomKey}`;
}

/**
 * sessionStorage throws (not returns null) in Safari private mode and under some
 * embedded webviews. Resume is a convenience, never a requirement — every access
 * degrades to "no record".
 */
function safeStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readResumeState(roomKey: string | null): ResumeState | null {
  if (!roomKey) return null;
  const storage = safeStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(storageKey(roomKey));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ResumeState> | null;
    // Validate rather than trust: a stale or hand-edited record must not be able
    // to push an unknown mode into the consultation.
    if (!parsed || !MODES.includes(parsed.mode as CommunicationMode)) return null;

    return {
      mode: parsed.mode as CommunicationMode,
      consented: typeof parsed.consented === 'boolean' ? parsed.consented : null,
    };
  } catch {
    return null;
  }
}

export function writeResumeState(roomKey: string | null, state: ResumeState): void {
  if (!roomKey) return;
  const storage = safeStorage();
  if (!storage) return;

  try {
    storage.setItem(storageKey(roomKey), JSON.stringify(state));
  } catch {
    /* quota / private mode — resume is best-effort */
  }
}

/** Drop the record once the appointment is over (ended, or gone server-side). */
export function clearResumeState(roomKey: string | null): void {
  if (!roomKey) return;
  const storage = safeStorage();
  if (!storage) return;

  try {
    storage.removeItem(storageKey(roomKey));
  } catch {
    /* nothing to do */
  }
}
